import React, { useCallback, useEffect, useState } from 'react';
import {
    AdminUser,
    listAdminUsers,
    revokeAdmin,
    updateAdminDisplayName,
} from '../../api/adminuser';
import { demoteAdmin } from '../../api/access';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import Pagination from '../../components/Pagination';
import UserPermissionModal from './UserPermissionModal';
import { confirmDialog, alertDialog, promptDialog } from '../../utils/dialog';

const PAGE_SIZE = 20;

function AdminUserList() {
    const [input, setInput] = useState('');
    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(1);
    const [list, setList] = useState<AdminUser[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [permEmail, setPermEmail] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listAdminUsers({
                keyword,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [keyword, page]);

    useEffect(() => {
        load();
    }, [load]);

    // 输入即搜 (2026-09-12): 停止输入 300ms 后自动按关键词过滤, 不必点「查询」; 清空即恢复全量.
    useEffect(() => {
        const t = window.setTimeout(() => {
            const kw = input.trim();
            if (kw !== keyword) {
                setKeyword(kw);
                setPage(1);
            }
        }, 300);
        return () => window.clearTimeout(t);
    }, [input, keyword]);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const next = input.trim();
        if (next === keyword && page === 1) {
            load();
            return;
        }
        setKeyword(next);
        setPage(1);
    };

    const onRevoke = async (email: string) => {
        if (!(await confirmDialog(`确认撤销 ${email} 的管理员权限?`))) return;
        try {
            await revokeAdmin(email);
            await load();
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);

            alertDialog(`撤销失败: ${msg}`);
        }
    };

    const onDemote = async (email: string) => {
        if (
            !(await confirmDialog(
                `确认将 ${email} 转为普通用户?其访问权限保留, 但不再有管理员权限。`,
            ))
        )
            return;
        try {
            await demoteAdmin(email);
            await load();
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`转为普通用户失败: ${msg}`);
        }
    };

    const onRenameInline = async (email: string, current: string) => {
        const next = await promptDialog(
            `修改 ${email} 的显示名`,
            current || '',
        );
        if (next === null) return; // 取消
        try {
            await updateAdminDisplayName(email, next.trim());
            await load();
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);

            alertDialog(`修改失败: ${msg}`);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">管理员</div>
                    <div className="text-xs text-slate-500">
                        管理员由「普通用户」页提升而来 (平台访问需先申请审批) ·
                        共 {total} 人
                    </div>
                </div>
                <form onSubmit={onSearch} className="flex gap-2 items-center">
                    <input
                        className="inp w-64"
                        placeholder="搜索 邮箱 / 显示名"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onBlur={() => {
                            // 清空后失焦直接恢复默认列表, 不必再点"查询"
                            if (input.trim() === '' && keyword !== '') {
                                setKeyword('');
                                setPage(1);
                            }
                        }}
                    />
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                </form>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th">邮箱</th>
                            <th className="th">显示名</th>
                            <th className="th">授予人</th>
                            <th className="th">授予时间</th>
                            <th className="th">备注</th>
                            <th className="th">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={6}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={6}
                                >
                                    暂无管理员,请在「普通用户」页把已获访问权限的用户提升为管理员
                                </td>
                            </tr>
                        )}
                        {list.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50">
                                <td className="td font-mono text-xs">
                                    {u.email}
                                </td>
                                <td className="td">{u.displayName || '—'}</td>
                                <td className="td text-xs text-slate-500">
                                    {u.reviewedByName || u.reviewedBy || '—'}
                                </td>
                                <td className="td text-xs text-slate-500">
                                    {formatTime(u.reviewedAt || u.createdAt)}
                                </td>
                                <td className="td text-xs text-slate-500">
                                    {u.remark || '—'}
                                </td>
                                <td className="td text-xs space-x-3 whitespace-nowrap">
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={() => setPermEmail(u.email)}
                                    >
                                        权限分配
                                    </button>
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={() =>
                                            onRenameInline(
                                                u.email,
                                                u.displayName || '',
                                            )
                                        }
                                    >
                                        改显示名
                                    </button>
                                    <button
                                        type="button"
                                        className="text-brand-600 hover:underline"
                                        onClick={() => onDemote(u.email)}
                                    >
                                        转为普通用户
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-danger-text"
                                        onClick={() => onRevoke(u.email)}
                                    >
                                        撤销
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>
            <UserPermissionModal
                email={permEmail}
                onClose={() => setPermEmail(null)}
            />
        </div>
    );
}

export default AdminUserList;
